import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Button,
  Icon,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@/ui';

import { MemberCohortTagsCell } from './CohortTagsCell';
import ViewerSessionZoneLabel from './ViewerSessionZoneLabel';

import type { MembersTableColumn } from './columns';
import type { MemberTableRow, MemberTableUpcomingSession } from './types';
import type { FC, MouseEvent, ReactNode } from 'react';

type CoachEntry = { avatarUrl?: string | null; id: string; name: string };

const renderCoachCell = (coachName: string | null, coaches?: CoachEntry[]) => {
  if (coaches && coaches.length > 1) {
    return (
      <div className="flex flex-col gap-1.5">
        {coaches.map((c) => (
          <div key={c.id} className="flex items-center gap-2">
            <Avatar
              firstName={c.name.split(' ')[0]}
              lastName={c.name.split(' ')[1] ?? ''}
              size="s"
              src={c.avatarUrl ?? ''}
            />
            <Typography
              className="!text-sm"
              color="text.secondary"
              variant="body2"
            >
              {c.name}
            </Typography>
          </div>
        ))}
      </div>
    );
  }
  if (coachName) {
    const coach = coaches?.[0];

    return (
      <div className="flex items-center gap-2">
        <Avatar
          firstName={coachName.split(' ')[0]}
          lastName={coachName.split(' ')[1] ?? ''}
          size="s"
          src={coach?.avatarUrl ?? ''}
        />
        <Typography className="!text-sm" color="text.secondary" variant="body2">
          {coachName}
        </Typography>
      </div>
    );
  }
  return (
    <Typography className="!text-sm" color="text.disabled" variant="body2">
      None assigned
    </Typography>
  );
};

const sessionTypeLabel = (type: MemberTableUpcomingSession['type']) => {
  if (type === 'chemistry') return 'Chemistry Call';
  if (type === 'biweekly') return 'Biweekly Session';
  if (type === 'single') return 'Single Session';
  return null;
};

const renderUpcomingSessionDetails = (session: MemberTableUpcomingSession) => {
  if (session.coach) {
    return (
      <div className="flex items-center gap-1.5">
        <Avatar
          firstName={session.coach.name.split(' ')[0]}
          lastName={session.coach.name.split(' ')[1] ?? ''}
          size="s"
          src={session.coach.avatarUrl ?? ''}
        />
        <Typography
          className="!text-xs"
          color="text.secondary"
          variant="caption"
        >
          {session.coach.name}
        </Typography>
      </div>
    );
  }

  const typeLabel = sessionTypeLabel(session.type);
  if (!typeLabel) return null;

  return (
    <Typography className="!text-xs" color="text.secondary" variant="caption">
      {typeLabel}
    </Typography>
  );
};

const renderUpcomingSession = (
  session: MemberTableUpcomingSession,
  index: number,
) => (
  <li
    key={`${session.startedAt}-${session.type}-${session.coach?.id ?? 'hidden'}-${index}`}
  >
    <Typography className="block !text-xs !font-semibold" variant="caption">
      {session.display.status === 'ready' ? (
        <>
          {session.display.dateTime}{' '}
          <ViewerSessionZoneLabel
            shortZone={session.display.shortZone}
            timeZone={session.display.timeZone}
          />
        </>
      ) : (
        session.display.text
      )}
    </Typography>
    {renderUpcomingSessionDetails(session)}
  </li>
);

const renderSessionsCell = (
  sessions: MemberTableRow['sessions'],
  sessionSummaryParts: string[],
) => {
  if (sessions.upcomingSessions.length > 0) {
    return (
      <>
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {sessions.upcomingSessions.slice(0, 2).map(renderUpcomingSession)}
        </ol>
        <Typography
          className="mt-1 block !text-xs"
          color="text.secondary"
          variant="caption"
        >
          {sessionSummaryParts.join(' • ')}
        </Typography>
      </>
    );
  }
  return (
    <>
      <Typography
        className="block !text-xs !font-semibold"
        style={{ color: '#BC4636' }}
        variant="caption"
      >
        No upcoming sessions
      </Typography>
      <Typography className="!text-xs" color="text.secondary" variant="caption">
        {sessionSummaryParts.join(' • ')}
      </Typography>
    </>
  );
};

const tableTextCell = (value: ReactNode) => (
  <Typography className="!text-sm" color="text.secondary" variant="body2">
    {value}
  </Typography>
);

const renderGrowthPlanStartedCell = (growthPlanStarted: boolean) =>
  growthPlanStarted ? (
    <Icon color="success">check</Icon>
  ) : (
    <Icon color="warning">close</Icon>
  );

const renderNameCell = (row: MemberTableRow) => {
  const { user } = row;

  return (
    <div className="flex min-w-[180px] items-center gap-2">
      <Avatar
        firstName={user.firstName}
        lastName={user.lastName}
        size="s"
        src={user.avatarUrl ?? ''}
      />
      <div className="min-w-0">
        <Typography
          className="truncate !text-sm !font-medium"
          variant="subtitle2"
        >
          {user.fullName}
        </Typography>
        <Typography
          className="block truncate !text-xs"
          color="text.secondary"
          variant="caption"
        >
          {user.profile?.title}
        </Typography>
      </div>
    </div>
  );
};

const AdminNoteCell = ({
  initialAdminNote,
  onSave,
  userId,
}: {
  initialAdminNote: string | null;
  onSave?: (userId: string, adminNote: string) => Promise<void> | void;
  userId: string;
}) => {
  const [adminNote, setAdminNote] = useState(initialAdminNote ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    setAdminNote(initialAdminNote ?? '');
  }, [initialAdminNote]);

  if (!onSave) {
    return (
      <Typography
        className="block max-w-[240px] whitespace-normal !text-sm"
        color="text.secondary"
        variant="body2"
      >
        {initialAdminNote || '—'}
      </Typography>
    );
  }

  const handleSaveNote = async () => {
    try {
      setIsSaving(true);
      setSaveError(false);
      await onSave(userId, adminNote);
      setShowSaved(true);
      window.setTimeout(() => setShowSaved(false), 1500);
    } catch (error) {
      console.error('Failed to save admin note', error);
      setSaveError(true);
      setShowSaved(false);
    } finally {
      setIsSaving(false);
    }
  };

  let buttonColor: 'error' | 'primary' | 'success' = 'primary';
  if (saveError) {
    buttonColor = 'error';
  } else if (showSaved) {
    buttonColor = 'success';
  }

  let buttonLabel = 'Save';
  if (saveError) {
    buttonLabel = 'Retry';
  } else if (showSaved) {
    buttonLabel = 'Saved!';
  }

  return (
    <div className="flex min-w-52 items-center gap-2">
      <TextField
        error={saveError}
        helperText={saveError ? 'Could not save. Try again.' : undefined}
        inputProps={{ style: { resize: 'vertical' } }}
        minRows={1}
        placeholder="Admin note"
        size="small"
        value={adminNote}
        multiline
        onChange={(event) => {
          setSaveError(false);
          setAdminNote(event.target.value);
        }}
      />
      <Button
        className="transition-colors duration-700"
        color={buttonColor}
        disabled={isSaving}
        size="small"
        variant="contained"
        onClick={handleSaveNote}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

const renderDefaultCell = (
  column: MembersTableColumn,
  row: MemberTableRow,
  sessionSummaryParts: string[],
  onAdminNoteSave?: (userId: string, adminNote: string) => Promise<void> | void,
) => {
  if (column.renderCell) {
    return column.renderCell(row);
  }

  switch (column.key) {
    case 'name':
      return renderNameCell(row);
    case 'company':
      return tableTextCell(row.company);
    case 'cohort_tags':
      return row.companyId ? (
        <MemberCohortTagsCell
          companyId={row.companyId}
          tags={row.cohortTags}
          userId={row.user.id}
        />
      ) : (
        tableTextCell('—')
      );
    case 'email':
      return (
        <Typography
          className="block max-w-[200px] truncate !text-sm"
          color="text.secondary"
          variant="body2"
        >
          {row.user.email}
        </Typography>
      );
    case 'member_status':
      return tableTextCell(row.status ?? '—');
    case 'date_paused':
      return tableTextCell(row.datePaused ?? '—');
    case 'return_date':
      return tableTextCell(row.returnDate ?? '—');
    case 'seat_type':
      return tableTextCell(row.seatType ?? '—');
    case 'joined_at':
      return tableTextCell(row.joined);
    case 'growth_plan_started':
      return renderGrowthPlanStartedCell(row.growthPlanStarted);
    case 'insights_status':
      return tableTextCell(row.insightsStatus);
    case 'contract_end_date':
      return tableTextCell(row.endDate ?? '—');
    case 'end_date_status':
      return tableTextCell(row.endDateStatus);
    case 'admin_note':
      return (
        <AdminNoteCell
          initialAdminNote={row.adminNote}
          userId={row.user.id}
          onSave={onAdminNoteSave}
        />
      );
    case 'coach_name':
      return renderCoachCell(row.coachName, row.coaches);
    case 'sessions':
      return renderSessionsCell(row.sessions, sessionSummaryParts);
    default:
      return null;
  }
};

interface MembersTableRowProps {
  columns: MembersTableColumn[];
  enableRowNavigation?: boolean;
  isAlt: boolean;
  row: MemberTableRow;
  rowHref?: (row: MemberTableRow) => string | null;
  onAdminNoteSave?: (userId: string, adminNote: string) => Promise<void> | void;
}

const defaultRowHref = (row: MemberTableRow) => `/coach/members/${row.user.id}`;

const MembersTableRowComponent: FC<MembersTableRowProps> = ({
  columns,
  enableRowNavigation = true,
  isAlt,
  row,
  rowHref = defaultRowHref,
  onAdminNoteSave,
}) => {
  const navigate = useNavigate();
  const { sessions } = row;
  const memberPath = rowHref(row);

  const sessionSummaryParts: string[] = [];
  sessionSummaryParts.push(`${sessions.upcoming} upcoming`);
  if (sessions.completed > 0)
    sessionSummaryParts.push(`${sessions.completed} completed`);
  if (sessions.chemCalls > 0)
    sessionSummaryParts.push(`${sessions.chemCalls} chem calls`);
  else sessionSummaryParts.push('No chem calls');

  const rowBg = isAlt ? 'bg-cement-50' : '';
  const isNavigable = enableRowNavigation && Boolean(memberPath);

  const openMemberInNewTab = () => {
    if (memberPath) {
      window.open(memberPath, '_blank', 'noopener,noreferrer');
    }
  };

  const isInteractiveTarget = (event: MouseEvent<HTMLTableRowElement>) =>
    event.target instanceof HTMLElement &&
    Boolean(event.target.closest('button, input, textarea, select, a'));

  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (!isNavigable || !memberPath || isInteractiveTarget(event)) return;

    if (event.metaKey || event.ctrlKey) {
      openMemberInNewTab();
      return;
    }

    navigate(memberPath);
  };

  const handleAuxClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (!isNavigable || isInteractiveTarget(event)) return;

    if (event.button === 1) {
      openMemberInNewTab();
    }
  };

  return (
    <TableRow
      className={`${isNavigable ? 'cursor-pointer hover:bg-sage-200' : ''} transition-colors ${rowBg}`}
      onAuxClick={handleAuxClick}
      onClick={handleClick}
    >
      {columns.map((column) => (
        <TableCell
          key={`${row.user.id}-${column.key}`}
          className={`!px-3 !py-2.5 ${column.key === 'coach_name' || column.key === 'cohort_tags' ? '!whitespace-nowrap' : ''} ${column.key === 'sessions' ? '!whitespace-normal break-words' : ''}`}
          style={
            column.key === 'sessions'
              ? { minWidth: 260, whiteSpace: 'normal' }
              : undefined
          }
        >
          {renderDefaultCell(column, row, sessionSummaryParts, onAdminNoteSave)}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default MembersTableRowComponent;
